import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Listing } from '@shared/types';
import ListingCard from '../components/ListingCard';
import { listingMatchesBrowseCity, BROWSE_CITIES } from '../lib/locationRegions';
import { usePublishedListings } from '../hooks/usePublishedListings';

const LOCATIONS = [...BROWSE_CITIES];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = searchParams.get('location') || '';
  const qFromUrl = searchParams.get('q') || '';
  const guestsParam = parseInt(searchParams.get('guests') || '0', 10) || 0;
  const minPriceParam = parseInt(searchParams.get('minPrice') || '0', 10) || 0;
  const maxPriceParam = parseInt(searchParams.get('maxPrice') || '0', 10) || 0;

  const [locationFilter, setLocationFilter] = useState(location);
  const [unitSearchFilter, setUnitSearchFilter] = useState(qFromUrl);
  const [guestsFilter, setGuestsFilter] = useState(guestsParam || 2);
  const [minPriceFilter, setMinPriceFilter] = useState(minPriceParam || 0);
  const [maxPriceFilter, setMaxPriceFilter] = useState(maxPriceParam || 0);

  useEffect(() => {
    setLocationFilter(location);
    setUnitSearchFilter(qFromUrl);
    setGuestsFilter(guestsParam || 2);
    setMinPriceFilter(minPriceParam || 0);
    setMaxPriceFilter(maxPriceParam || 0);
  }, [location, qFromUrl, guestsParam, minPriceParam, maxPriceParam]);

  const { data: listings, isLoading, error } = usePublishedListings();

  const sortedListings = useMemo(
    () =>
      [...(listings || [])].sort(
        (a, b) => ((a as Listing).basePrice || 0) - ((b as Listing).basePrice || 0)
      ),
    [listings]
  );

  const needle = unitSearchFilter.trim().toLowerCase();
  const minCents = minPriceFilter > 0 ? minPriceFilter * 100 : 0;
  const maxCents = maxPriceFilter > 0 ? maxPriceFilter * 100 : 0;

  const filteredListings =
    sortedListings?.filter((listing) => {
      const anyListing = listing as Listing;
      const locNorm = (locationFilter || location || '').trim();
      const locationOk = !locNorm || listingMatchesBrowseCity(anyListing, locNorm);
      const capacityOk =
        !guestsFilter || typeof anyListing.capacity !== 'number'
          ? true
          : anyListing.capacity >= guestsFilter;
      const price = (anyListing.basePrice as number) || 0;
      const minOk = !minCents || price >= minCents;
      const maxOk = !maxCents || price <= maxCents;

      if (!needle) return locationOk && capacityOk && minOk && maxOk;
      const slug = (anyListing.slug || '').toLowerCase();
      const tags = (anyListing.tags || []).join(' ').toLowerCase();
      const blob = [
        anyListing.title,
        anyListing.description || '',
        anyListing.location?.city,
        anyListing.location?.address,
        anyListing.id,
        slug,
        tags,
      ]
        .join(' ')
        .toLowerCase();
      const textOk = blob.includes(needle);
      return locationOk && capacityOk && minOk && maxOk && textOk;
    }) || [];

  const handleApplyFilters = () => {
    const next = new URLSearchParams();
    if (locationFilter) next.set('location', locationFilter);
    if (unitSearchFilter.trim()) next.set('q', unitSearchFilter.trim());
    if (guestsFilter) next.set('guests', String(guestsFilter));
    if (minPriceFilter) next.set('minPrice', String(minPriceFilter));
    if (maxPriceFilter) next.set('maxPrice', String(maxPriceFilter));
    setSearchParams(next);
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32373c]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-6">Search Results</h1>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search by property name, area, or unit
          </label>
          <input
            type="search"
            className="input w-full"
            placeholder="e.g. Bronze Bay, sea view, or part of the title"
            value={unitSearchFilter}
            onChange={(e) => setUnitSearchFilter(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select
              className="input"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="">All locations</option>
              {LOCATIONS.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Guests</label>
            <select
              className="input"
              value={guestsFilter}
              onChange={(e) => setGuestsFilter(parseInt(e.target.value, 10) || 1)}
            >
              {Array.from({ length: 8 }, (_, i) => i + 1).map((g) => (
                <option key={g} value={g}>
                  {g} guest{g > 1 ? 's' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min price (R)</label>
            <input
              type="number"
              className="input"
              value={minPriceFilter || ''}
              onChange={(e) => setMinPriceFilter(parseInt(e.target.value, 10) || 0)}
              min={0}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max price (R)</label>
            <input
              type="number"
              className="input"
              value={maxPriceFilter || ''}
              onChange={(e) => setMaxPriceFilter(parseInt(e.target.value, 10) || 0)}
              min={0}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleApplyFilters}
          className="btn btn-primary w-full mt-4 font-heading"
        >
          Apply filters
        </button>
      </div>
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800">
            {error instanceof Error && error.message.includes('index')
              ? 'Search index is being created. Please try again in a few moments.'
              : 'Error loading listings. Please try again.'}
          </p>
        </div>
      )}
      {filteredListings && filteredListings.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredListings.map((listing) => (
            <ListingCard key={listing.id} listing={listing} />
          ))}
        </div>
      ) : !isLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg mb-4">No listings found</p>
          <p className="text-gray-500 text-sm">
            {needle || location
              ? 'Try clearing the property search or location filter, or check spelling.'
              : 'Make sure your listings are set to "published" status in the admin panel.'}
          </p>
        </div>
      ) : null}
    </div>
  );
}

